import React, { useState, ChangeEvent, useEffect } from "react";
import { useDispatch } from "react-redux";

import { useSetCoverImageFile } from "src/app/components/CroppedImageUploader/utils/useUploadCoverImage";
import { ProjectSetupType, type SelectOption, ProjectAction } from "../types";
import { useSyncAIAttributes } from "./useSyncAIAttributes";
import { useSubmitProjectSetup } from "./useSubmitProjectSetup";
import useProjectValidation from "./useProjectValidation";
import { getProjectTemplateFields } from "../utils";
import {
  clearGlobalMetaFieldProjectWizardFields,
  setGlobalMetaFieldProjectWizardFields,
} from "src/redux";
import {
  clearCreateNewProjectData,
  setBriefDataForNewProject,
} from "src/redux/main/mainActions";
import useGetCurrencies from "src/hooks/useGetCurrencies";

export default function useCreateProjectLogic(
  projectAction: ProjectAction,
  projectId: string,
) {
  const [projectName, setProjectName] = useState("");
  const [selectedCurrencyOption, setSelectedCurrencyOption] = useState<
    string | null
  >(null);
  const [selectedBaseTemplateOption, setSelectedBaseTemplateOption] =
    useState<SelectOption | null>(null);
  const [selectedExistingProjectOption, setSelectedExistingProjectOption] =
    useState<SelectOption | null>(null);
  const [
    selectedSocialPlatformTemplateOptions,
    setSelectedSocialPlatformTemplateOptions,
  ] = useState<SelectOption[]>([]);
  const [currentProjectSetupOption, setCurrentProjectSetupOption] =
    useState<ProjectSetupType>(ProjectSetupType.Template);

  const { coverImageFile, setNewImage } = useSetCoverImageFile();

  const { currencies } = useGetCurrencies();

  const currencyOptions = currencies.map((currency) => ({
    key: currency.code,
    label: (
      <span className="currency-option">
        <span className="currency-code">{currency.code}</span>
        {currency.name}
      </span>
    ),
    value: currency.code,
  }));

  const handleChangeProjectName = (e: ChangeEvent<HTMLInputElement>) => {
    setProjectName(e.target.value.trimStart());
  };

  const dispatch = useDispatch();

  const {
    updatedProjectTemplates,
    allBaseOptions,
    allSocialPlatformTemplateOptions,
  } = useSyncAIAttributes({
    projectAction,
    setProjectName,
    setNewImage,
    setCurrentProjectSetupOption,
    setSelectedBaseTemplateOption,
    setSelectedSocialPlatformTemplateOptions,
    setSelectedCurrencyOption,
    currencies,
  });

  const { submitProjectSetup, isCreatingProject } = useSubmitProjectSetup({
    projectId,
    projectAction,
    projectName,
    selectedCurrencyOption,
    selectedBaseTemplateOption,
    selectedExistingProjectOption,
    selectedSocialPlatformTemplateOptions,
    currentProjectSetupOption,
    coverImageFile,
    updatedProjectTemplates,
  });

  const { hasUnsavedChanges, createCampaignButtonDisabled } =
    useProjectValidation({
      projectAction,
      projectName,
      selectedCurrencyOption,
      selectedBaseTemplateOption,
      selectedExistingProjectOption,
      selectedSocialPlatformTemplateOptions,
      coverImageFile,
      currentProjectSetupOption,
    });

  useEffect(() => {
    const requiredFields = getProjectTemplateFields(
      selectedBaseTemplateOption,
      selectedSocialPlatformTemplateOptions,
      updatedProjectTemplates,
    );

    dispatch(setGlobalMetaFieldProjectWizardFields(requiredFields));
  }, [
    selectedBaseTemplateOption,
    selectedSocialPlatformTemplateOptions,
    updatedProjectTemplates,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearGlobalMetaFieldProjectWizardFields());
      dispatch(clearCreateNewProjectData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (projectAction === "create") {
      return () => {
        dispatch(setBriefDataForNewProject(null));
      };
    }
  }, [dispatch, projectAction]);

  return {
    formState: {
      projectName,
      selectedCurrencyOption,
      selectedBaseTemplateOption,
      selectedExistingProjectOption,
      selectedSocialPlatformTemplateOptions,
      currentProjectSetupOption,
      coverImageFile,
      isCreatingProject,
    },
    formOptions: {
      allBaseOptions,
      allSocialPlatformTemplateOptions,
      currencyOptions,
    },
    formActions: {
      submitProjectSetup,
      handleChangeProjectName,
      setSelectedCurrencyOption,
      setSelectedBaseTemplateOption,
      setSelectedExistingProjectOption,
      setCurrentProjectSetupOption,
      setNewImage,
      setSelectedSocialPlatformTemplateOptions,
    },
    formValidation: {
      hasUnsavedChanges,
      createCampaignButtonDisabled,
    },
  };
}
